'use client'

import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { useRouter } from 'next/navigation'
import { useToast } from '@/components/ui/toast'

// =============================================================================
// Types
// =============================================================================

type CommandCategory =
  | 'navigation'
  | 'actions'
  | 'settings'
  | 'smart-home'
  | 'ai-query'
  | 'memory'
  | 'health'
  | 'recent'

interface Command {
  id: string
  name: string
  description: string
  shortcut?: string
  icon: string
  action: () => void | Promise<void>
  category: CommandCategory
  keywords?: string[]
}

interface AIQueryResult {
  type: 'answer' | 'action' | 'navigation' | 'smart-home' | 'ai-query'
  content: string
  action?: () => void
  confidence: number
}

interface SmartHomeQuickAction {
  id: string
  name: string
  icon: string
  action: () => Promise<void>
}

// =============================================================================
// Natural Language Query Patterns
// =============================================================================

const NL_PATTERNS = {
  smartHome: [
    /turn (on|off) (the )?(lights?|fan|ac|tv|thermostat)/i,
    /set (the )?(temperature|thermostat) to (\d+)/i,
    /(lock|unlock) (the )?(doors?|front door|back door)/i,
    /what('s| is) the (temperature|humidity|status) (in|of)/i,
    /is (the )?(door|window|garage) (open|closed|locked)/i,
    /arm|disarm (the )?(alarm|security)/i,
    /(home|away|sleep|movie|focus|vacation) mode/i,
  ],
  memory: [
    /what (do i|did i) (like|prefer|want|need)/i,
    /remind me (about|of)/i,
    /what (was|were) my (goal|goals|preference|preferences)/i,
    /do you remember/i,
    /what did (i|we) (talk|discuss|say) about/i,
  ],
  search: [
    /find (me )?(entries?|notes?|links?|articles?) (about|on|related to)/i,
    /search (for )?/i,
    /show me (my )?(recent|latest|old)/i,
    /what (do i|did i) save (about|on)/i,
  ],
  health: [
    /how (did i|was my) sleep/i,
    /what('s| is) my (weight|calories|steps|heart rate)/i,
    /log (my )?(weight|water|meal|workout|sleep)/i,
    /start (a )?(experiment|health experiment)/i,
    /how('s| is) my (health|wellness|progress)/i,
  ],
}

// =============================================================================
// Component
// =============================================================================

export function CommandPalette() {
  const router = useRouter()
  const { success, error: showError } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isProcessingAI, setIsProcessingAI] = useState(false)
  const [aiResult, setAiResult] = useState<AIQueryResult | null>(null)
  const [recentCommands, setRecentCommands] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  // Load recent commands from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('linkbox-recent-commands')
    if (stored) {
      try {
        setRecentCommands(JSON.parse(stored))
      } catch {}
    }
  }, [])

  // Save to recent commands
  const addToRecent = useCallback((commandId: string) => {
    setRecentCommands(prev => {
      const updated = [commandId, ...prev.filter(id => id !== commandId)].slice(0, 5)
      localStorage.setItem('linkbox-recent-commands', JSON.stringify(updated))
      return updated
    })
  }, [])

  // =============================================================================
  // Smart Home Quick Actions
  // =============================================================================

  const executeSmartHomeAction = async (action: string, params?: Record<string, unknown>) => {
    try {
      const response = await fetch('/api/smart-home/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...params })
      })
      if (response.ok) {
        success(`Smart home: ${action} executed`)
      } else {
        showError('Failed to execute smart home action')
      }
    } catch {
      showError('Smart home service unavailable')
    }
  }

  // =============================================================================
  // AI Query Processing
  // =============================================================================

  const processNaturalLanguageQuery = async (query: string): Promise<AIQueryResult | null> => {
    const lowerQuery = query.toLowerCase()

    // Check for smart home patterns
    for (const pattern of NL_PATTERNS.smartHome) {
      if (pattern.test(query)) {
        // Handle mode commands
        const modeMatch = query.match(/(home|away|sleep|movie|focus|vacation) mode/i)
        if (modeMatch) {
          return {
            type: 'smart-home',
            content: `Activating ${modeMatch[1]} mode...`,
            action: () => executeSmartHomeAction('activateMode', { mode: modeMatch[1].toLowerCase() }),
            confidence: 0.95
          }
        }

        // Handle light commands
        const lightMatch = query.match(/turn (on|off) (the )?(lights?)/i)
        if (lightMatch) {
          return {
            type: 'smart-home',
            content: `Turning ${lightMatch[1]} the lights...`,
            action: () => executeSmartHomeAction(lightMatch[1] === 'on' ? 'turnOnLights' : 'turnOffLights'),
            confidence: 0.95
          }
        }

        // Handle thermostat
        const tempMatch = query.match(/set (the )?(temperature|thermostat) to (\d+)/i)
        if (tempMatch) {
          return {
            type: 'smart-home',
            content: `Setting temperature to ${tempMatch[3]}°...`,
            action: () => executeSmartHomeAction('setTemperature', { temperature: parseInt(tempMatch[3]) }),
            confidence: 0.95
          }
        }

        // Generic smart home query - go to smart home page
        return {
          type: 'navigation',
          content: 'Opening Smart Home Command Center...',
          action: () => router.push('/smart-home'),
          confidence: 0.8
        }
      }
    }

    // Check for memory/remember patterns
    for (const pattern of NL_PATTERNS.memory) {
      if (pattern.test(query)) {
        return {
          type: 'navigation',
          content: 'Searching your memories...',
          action: () => router.push(`/rag?q=${encodeURIComponent(query)}`),
          confidence: 0.85
        }
      }
    }

    // Check for health patterns
    for (const pattern of NL_PATTERNS.health) {
      if (pattern.test(query)) {
        const experimentMatch = query.match(/start (a )?(experiment|health experiment)/i)
        if (experimentMatch) {
          return {
            type: 'navigation',
            content: 'Opening Health Experiments...',
            action: () => router.push('/health/experiments'),
            confidence: 0.9
          }
        }
        return {
          type: 'navigation',
          content: 'Opening Health Dashboard...',
          action: () => router.push('/health'),
          confidence: 0.85
        }
      }
    }

    // Check for search patterns
    for (const pattern of NL_PATTERNS.search) {
      if (pattern.test(query)) {
        return {
          type: 'navigation',
          content: `Searching for: ${query}`,
          action: () => router.push(`/search?q=${encodeURIComponent(query)}`),
          confidence: 0.8
        }
      }
    }

    // If no pattern matches but query is long enough, treat as AI search
    if (query.length > 10) {
      return {
        type: 'ai-query',
        content: `Ask AI: "${query}"`,
        action: () => router.push(`/rag?q=${encodeURIComponent(query)}`),
        confidence: 0.6
      }
    }

    return null
  }

  // =============================================================================
  // Commands Definition
  // =============================================================================

  const commands: Command[] = [
    // Navigation
    { id: 'nav-dashboard', name: 'Go to Dashboard', description: 'View your dashboard', icon: '🏠', category: 'navigation', action: () => router.push('/dashboard'), keywords: ['home', 'main'] },
    { id: 'nav-collections', name: 'Go to Collections', description: 'Manage your collections', icon: '📁', category: 'navigation', action: () => router.push('/dashboard/collections'), keywords: ['folders', 'organize'] },
    { id: 'nav-entries', name: 'Go to Entries', description: 'View all entries', icon: '📝', category: 'navigation', action: () => router.push('/dashboard/entries'), keywords: ['notes', 'links', 'saved'] },
    { id: 'nav-search', name: 'Go to Search', description: 'Search your content', icon: '🔍', category: 'navigation', action: () => router.push('/search'), keywords: ['find', 'query'] },
    { id: 'nav-rag', name: 'Ask AI', description: 'Ask questions about your data', icon: '🤖', category: 'navigation', action: () => router.push('/rag'), keywords: ['ai', 'question', 'chat', 'assistant'] },
    { id: 'nav-health', name: 'Go to Health', description: 'View health dashboard', icon: '❤️', category: 'navigation', action: () => router.push('/health'), keywords: ['wellness', 'fitness', 'medical'] },
    { id: 'nav-finance', name: 'Go to Finance', description: 'View finance dashboard', icon: '💰', category: 'navigation', action: () => router.push('/finance'), keywords: ['money', 'budget', 'spending'] },
    { id: 'nav-analytics', name: 'Go to Analytics', description: 'View analytics', icon: '📊', category: 'navigation', action: () => router.push('/analytics'), keywords: ['stats', 'data', 'insights'] },
    { id: 'nav-collaboration', name: 'Go to Collaboration', description: 'Collaborate with team', icon: '👥', category: 'navigation', action: () => router.push('/collaboration'), keywords: ['team', 'share', 'together'] },
    { id: 'nav-graph', name: 'Go to Knowledge Graph', description: 'Visualize connections', icon: '🕸️', category: 'navigation', action: () => router.push('/knowledge-graph'), keywords: ['network', 'relationships', 'map'] },
    { id: 'nav-settings', name: 'Go to Settings', description: 'Manage settings', icon: '⚙️', category: 'navigation', action: () => router.push('/dashboard/settings'), keywords: ['preferences', 'config'] },
    { id: 'nav-profile', name: 'Go to Profile', description: 'View your profile', icon: '👤', category: 'navigation', action: () => router.push('/profile'), keywords: ['account', 'user'] },
    { id: 'nav-export-import', name: 'Export & Import', description: 'Backup or restore data', icon: '💾', category: 'navigation', action: () => router.push('/export-import'), keywords: ['backup', 'data'] },

    // Smart Home Commands
    { id: 'smart-home-center', name: 'Smart Home Center', description: 'Control your smart home', icon: '🏡', category: 'smart-home', action: () => router.push('/smart-home'), keywords: ['iot', 'devices', 'home'] },
    { id: 'smart-home-away', name: 'Away Mode', description: 'Activate away mode', icon: '🚗', category: 'smart-home', action: () => executeSmartHomeAction('activateMode', { mode: 'away' }), keywords: ['leaving', 'out'] },
    { id: 'smart-home-home', name: 'Home Mode', description: 'Activate home mode', icon: '🏠', category: 'smart-home', action: () => executeSmartHomeAction('activateMode', { mode: 'home' }), keywords: ['arrived', 'back'] },
    { id: 'smart-home-sleep', name: 'Sleep Mode', description: 'Activate sleep mode', icon: '🌙', category: 'smart-home', action: () => executeSmartHomeAction('activateMode', { mode: 'sleep' }), keywords: ['night', 'bedtime'] },
    { id: 'smart-home-movie', name: 'Movie Mode', description: 'Activate movie mode', icon: '🎬', category: 'smart-home', action: () => executeSmartHomeAction('activateMode', { mode: 'movie' }), keywords: ['cinema', 'watch'] },

    // Health Commands
    { id: 'health-experiments', name: 'Health Experiments', description: 'Start or view health experiments', icon: '🧪', category: 'health', action: () => router.push('/health/experiments'), keywords: ['test', 'track', 'analyze'] },
    { id: 'health-log-weight', name: 'Log Weight', description: 'Record your weight', icon: '⚖️', category: 'health', action: () => { router.push('/health?action=log-weight'); success('Opening weight log...') }, keywords: ['scale', 'body'] },
    { id: 'health-log-water', name: 'Log Water', description: 'Record water intake', icon: '💧', category: 'health', action: () => { router.push('/health?action=log-water'); success('Opening water log...') }, keywords: ['drink', 'hydration'] },
    { id: 'health-log-meal', name: 'Log Meal', description: 'Record a meal', icon: '🍽️', category: 'health', action: () => { router.push('/health?action=log-meal'); success('Opening meal log...') }, keywords: ['food', 'eat', 'calories'] },

    // Memory & AI Commands
    { id: 'memory-digest', name: 'Memory Lane', description: 'View rediscovered content', icon: '📚', category: 'memory', action: () => router.push('/dashboard?view=memory-lane'), keywords: ['resurface', 'forgotten', 'old'] },
    { id: 'memory-profile', name: 'What AI Knows About Me', description: 'View your AI memory profile', icon: '🧠', category: 'memory', action: () => router.push('/profile/ai-memory'), keywords: ['remember', 'preferences'] },

    // Actions
    { id: 'action-new-entry', name: 'New Entry', description: 'Create a new entry', icon: '➕', shortcut: '⌘+N', category: 'actions', action: () => success('Opening new entry modal...'), keywords: ['add', 'create'] },
    { id: 'action-new-collection', name: 'New Collection', description: 'Create a new collection', icon: '📁', shortcut: '⌘+⇧+N', category: 'actions', action: () => success('Opening new collection modal...'), keywords: ['folder', 'organize'] },
    { id: 'action-upload', name: 'Upload File', description: 'Upload a file', icon: '📤', shortcut: '⌘+U', category: 'actions', action: () => success('Opening upload modal...'), keywords: ['import', 'add'] },
    { id: 'action-search', name: 'Search', description: 'Search your content', icon: '🔍', shortcut: '⌘+F', category: 'actions', action: () => router.push('/search'), keywords: ['find'] },

    // Settings
    { id: 'settings-theme', name: 'Toggle Theme', description: 'Switch between light/dark mode', icon: '🌓', shortcut: '⌘+⇧+T', category: 'settings', action: () => success('Theme toggled'), keywords: ['dark', 'light', 'mode'] },
    { id: 'settings-help', name: 'Help & Documentation', description: 'View help docs', icon: '❓', shortcut: '?', category: 'settings', action: () => success('Opening help...'), keywords: ['support', 'guide'] },
    { id: 'settings-api-keys', name: 'API Keys', description: 'Manage your API keys', icon: '🔑', category: 'settings', action: () => router.push('/dashboard/settings/api-keys'), keywords: ['openai', 'keys'] },
  ]

  // =============================================================================
  // Filtering & Grouping
  // =============================================================================

  const filteredCommands = search
    ? commands.filter(cmd =>
        cmd.name.toLowerCase().includes(search.toLowerCase()) ||
        cmd.description.toLowerCase().includes(search.toLowerCase()) ||
        cmd.keywords?.some(kw => kw.toLowerCase().includes(search.toLowerCase()))
      )
    : commands

  // Add recent commands at the top when no search
  const commandsWithRecent = !search && recentCommands.length > 0
    ? [
        ...recentCommands
          .map(id => commands.find(c => c.id === id))
          .filter((c): c is Command => c !== undefined)
          .map(c => ({ ...c, category: 'recent' as CommandCategory })),
        ...commands
      ]
    : filteredCommands

  const categoryOrder: CommandCategory[] = ['recent', 'ai-query', 'smart-home', 'health', 'memory', 'navigation', 'actions', 'settings']

  const categoryLabels: Record<CommandCategory, string> = {
    'recent': 'Recent',
    'ai-query': 'AI Query',
    'smart-home': 'Smart Home',
    'health': 'Health',
    'memory': 'Memory & AI',
    'navigation': 'Navigation',
    'actions': 'Quick Actions',
    'settings': 'Settings'
  }

  const groupedCommands = (search ? filteredCommands : commandsWithRecent).reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = []
    acc[cmd.category].push(cmd)
    return acc
  }, {} as Record<string, Command[]>)

  // Sort groups by category order
  const sortedGroups = Object.entries(groupedCommands).sort((a, b) => {
    return categoryOrder.indexOf(a[0] as CommandCategory) - categoryOrder.indexOf(b[0] as CommandCategory)
  })

  // Process AI query when search changes
  useEffect(() => {
    const processQuery = async () => {
      if (search.length > 3 && filteredCommands.length === 0) {
        setIsProcessingAI(true)
        const result = await processNaturalLanguageQuery(search)
        setAiResult(result)
        setIsProcessingAI(false)
      } else if (search.length > 10) {
        // Also check for NL patterns even if commands match
        const result = await processNaturalLanguageQuery(search)
        setAiResult(result)
      } else {
        setAiResult(null)
      }
    }

    const debounce = setTimeout(processQuery, 300)
    return () => clearTimeout(debounce)
  }, [search])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette (⌘+K or Ctrl+K)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
        return
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setSearch('')
        setSelectedIndex(0)
        return
      }

      // Arrow navigation when open
      if (isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % filteredCommands.length)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedIndex(prev => prev === 0 ? filteredCommands.length - 1 : prev - 1)
        } else if (e.key === 'Enter') {
          e.preventDefault()
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action()
            setIsOpen(false)
            setSearch('')
            setSelectedIndex(0)
          }
        }
        return
      }

      // Global shortcuts (when palette is closed)
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        success('New entry shortcut triggered')
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'N') {
        e.preventDefault()
        success('New collection shortcut triggered')
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'u') {
        e.preventDefault()
        success('Upload shortcut triggered')
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        router.push('/search')
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault()
        success('Theme toggle shortcut triggered')
      } else if (e.key === '?') {
        e.preventDefault()
        success('Help shortcut triggered')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, filteredCommands, selectedIndex, router, success])

  const executeCommand = async (command: Command) => {
    addToRecent(command.id)
    await command.action()
    setIsOpen(false)
    setSearch('')
    setSelectedIndex(0)
    setAiResult(null)
  }

  const executeAIResult = async () => {
    if (aiResult?.action) {
      await aiResult.action()
      setIsOpen(false)
      setSearch('')
      setSelectedIndex(0)
      setAiResult(null)
    }
  }

  // Get all items for navigation (commands + AI result if present)
  const allNavigableItems = aiResult
    ? [{ id: 'ai-result', isAI: true }, ...filteredCommands.map(c => ({ id: c.id, isAI: false }))]
    : filteredCommands.map(c => ({ id: c.id, isAI: false }))

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Command Palette */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setSelectedIndex(0)
            }}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
            autoFocus
          />
          <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded">
            ESC
          </kbd>
        </div>

        {/* Commands List */}
        <div className="max-h-96 overflow-y-auto">
          {/* AI Query Result */}
          {aiResult && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="px-4 py-2 text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider bg-purple-50 dark:bg-purple-900/20 flex items-center gap-2">
                <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                AI Suggestion
              </div>
              <button
                onClick={executeAIResult}
                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors ${
                  selectedIndex === 0 ? 'bg-purple-50 dark:bg-purple-900/30 ring-2 ring-purple-500' : ''
                }`}
              >
                <span className="text-2xl">
                  {aiResult.type === 'smart-home' ? '🏡' : aiResult.type === 'navigation' ? '🔗' : '🤖'}
                </span>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {aiResult.content}
                  </div>
                  <div className="text-xs text-purple-600 dark:text-purple-400">
                    Confidence: {Math.round(aiResult.confidence * 100)}% - Press Enter to execute
                  </div>
                </div>
                <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-purple-600 bg-purple-100 dark:bg-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700 rounded">
                  Enter
                </kbd>
              </button>
            </div>
          )}

          {/* Processing indicator */}
          {isProcessingAI && (
            <div className="px-4 py-3 flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Processing natural language query...</span>
            </div>
          )}

          {sortedGroups.length === 0 && !aiResult && !isProcessingAI ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              <p>No commands found</p>
              <p className="text-xs mt-2">Try typing a natural language query like &quot;turn on the lights&quot; or &quot;what are my goals?&quot;</p>
            </div>
          ) : (
            sortedGroups.map(([category, cmds]) => (
              <div key={category}>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 flex items-center gap-2">
                  {category === 'recent' && <span className="text-yellow-500">★</span>}
                  {category === 'smart-home' && <span className="text-green-500">●</span>}
                  {category === 'health' && <span className="text-red-500">●</span>}
                  {category === 'memory' && <span className="text-purple-500">●</span>}
                  {categoryLabels[category as CommandCategory] || category}
                </div>
                {cmds.map((command) => {
                  const globalIndex = aiResult
                    ? allNavigableItems.findIndex(item => !item.isAI && item.id === command.id)
                    : filteredCommands.indexOf(command)
                  const isSelected = globalIndex === selectedIndex
                  return (
                    <button
                      key={`${category}-${command.id}`}
                      onClick={() => executeCommand(command)}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                      }`}
                    >
                      <span className="text-2xl">{command.icon}</span>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {command.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {command.description}
                        </div>
                      </div>
                      {command.shortcut && (
                        <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded">
                          {command.shortcut}
                        </kbd>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">↵</kbd>
              Select
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-purple-500">AI-Powered</span>
            <span>•</span>
            <span>⌘+K</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Export for external use
// =============================================================================

export type { Command, CommandCategory, AIQueryResult }
